/**
 * Small shared pieces for the admin Users / Analytics / Launch tabs: stat
 * tiles, sparkline, bar and line charts (react-native-svg, no chart library),
 * pills, and formatting helpers. Dark admin palette; motion via the shared
 * motion primitives (off under reduced motion).
 *
 * Floodlight (v1.70): styles read --bo-adm-* variables (floodlight/vars/admin.js)
 * with the classic value as the fallback, so Classic is unchanged. `A` keeps
 * literal colours because callers concatenate alpha suffixes onto them; where
 * this file does that with the accent it uses precomposed alpha tokens.
 */
import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import AnimatedNumber from '../motion/AnimatedNumber';
import PressableScale from '../motion/PressableScale';
import Appear from '../motion/Appear';
import { uiVar } from '../floodlight/uiStyleCss';
import { AV, ADM_DISPLAY } from '../floodlight/vars/admin';

export const A = {
    ink: 'rgba(255,255,255,0.92)',
    soft: 'rgba(255,255,255,0.62)',
    faint: 'rgba(255,255,255,0.38)',
    line: 'rgba(255,255,255,0.08)',
    panel: 'rgba(255,255,255,0.035)',
    accent: '#a78bfa',
    green: '#34D399',
    amber: '#FBBF24',
    red: '#F87171',
    blue: '#60A5FA',
    mono: Platform.OS === 'web' ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
};

/** Accent-aware helpers: the purple accent becomes Floodlight green; every other colour passes through untouched. */
const isAccent = (c) => c === A.accent;
const fg = (c) => (isAccent(c) ? AV.accent : c);
/**
 * `${c}${alpha}`, except for the accent, where it is a variable. Its fallback is
 * the rgba() react-native-web would have produced from the 8-digit hex (alpha
 * rounded to 2 places), because a var() value skips RNW's colour normalising.
 */
const hexAlphaToRnw = (hex, alpha) => {
    const n = parseInt(hex.slice(1, 7), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${(parseInt(alpha, 16) / 255).toFixed(2)})`;
};
const tint = (c, alpha) => (isAccent(c) ? uiVar(`adm-accent-a${alpha}`, hexAlphaToRnw(c, alpha)) : `${c}${alpha}`);

export const PLAN_COLOR = { FREE: '#94a3b8', BASIC: '#60a5fa', PRO: '#a78bfa', ULTIMATE: '#fbbf24' };

const toMs = (v) => (v ? Date.parse(v) : NaN);

export function relTime(iso, now = Date.now()) {
    const t = toMs(iso);
    if (!Number.isFinite(t)) return '—';
    const s = Math.max(0, Math.round((now - t) / 1000));
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
    if (s < 86400 * 365) return `${Math.round(s / (86400 * 30))}mo ago`;
    return `${Math.round(s / (86400 * 365))}y ago`;
}

export const shortDate = (iso) => {
    const t = toMs(iso);
    return Number.isFinite(t) ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};

export const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export function Pill({ label, color = A.soft, bg, style }) {
    return (
        <View style={[s.pill, { backgroundColor: bg || tint(color, '22'), borderColor: tint(color, '55') }, style]}>
            <Text style={[s.pillText, { color: fg(color) }]} numberOfLines={1}>{label}</Text>
        </View>
    );
}

export function Stat({ label, value, format, hint, tone, index = 0 }) {
    const color = tone === 'good' ? A.green : tone === 'warn' ? A.amber : tone === 'bad' ? A.red : AV.ink;
    const isNum = typeof value === 'number' && Number.isFinite(value);
    return (
        <Appear index={index} style={s.stat}>
            <Text style={s.statLabel}>{label}</Text>
            {isNum
                ? <AnimatedNumber value={value} format={format} countUpOnMount style={[s.statValue, { color }]} />
                : <Text style={[s.statValue, { color: AV.faint }]}>{value ?? '—'}</Text>}
            {!!hint && <Text style={s.statHint}>{hint}</Text>}
        </Appear>
    );
}

export function Sparkline({ values = [], width = 84, height = 22, color = A.green }) {
    const max = Math.max(1, ...values);
    if (!values.length) return null;
    const step = values.length > 1 ? width / (values.length - 1) : width;
    const pts = values.map((v, i) => [i * step, height - 2 - (v / max) * (height - 4)]);
    const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
    const total = values.reduce((a, b) => a + b, 0);
    return (
        <Svg width={width} height={height} accessibilityLabel={`${total} searches in the last ${values.length} days`}>
            <Path d={`${d} L${width} ${height} L0 ${height} Z`} fill={color} fillOpacity={total ? 0.12 : 0} />
            <Path d={d} stroke={total ? color : A.line} strokeWidth={1.5} fill="none" strokeLinejoin="round" />
        </Svg>
    );
}

/**
 * Daily bars, one hue, baseline-anchored with 4px rounded tops and 2px gaps.
 * Hover / keyboard focus on a bar shows its exact value in the readout line
 * (the chart's tooltip); a missing day (no rollup) renders as a faint stub.
 */
export function BarChart({ data = [], valueKey, height = 150, color = A.accent, format = (v) => Number(v).toLocaleString('en-US'), unit = '', label }) {
    const [hover, setHover] = React.useState(null);
    const vals = data.map((d) => Number(d[valueKey]) || 0);
    const max = Math.max(1, ...vals);
    const total = vals.reduce((x, y) => x + y, 0);
    const h = hover != null ? data[hover] : null;
    return (
        <View accessibilityLabel={label} style={{ gap: 6 }}>
            <Text style={s.readout} accessibilityLiveRegion="polite">
                {h ? `${h.day}: ${h.missing ? 'no data' : `${format(vals[hover])}${unit}`}` : `${format(total)}${unit} total · peak ${format(max)}`}
            </Text>
            <View style={{ height, flexDirection: 'row', alignItems: 'flex-end', gap: 2, borderBottomWidth: 1, borderBottomColor: AV.line }}>
                {data.map((d, i) => (
                    <PressableScale
                        key={d.day || i}
                        lift={0}
                        scaleTo={1}
                        onHoverIn={() => setHover(i)}
                        onHoverOut={() => setHover((x) => (x === i ? null : x))}
                        onFocus={() => setHover(i)}
                        onBlur={() => setHover(null)}
                        onPress={() => setHover(i)}
                        accessibilityLabel={`${d.day}: ${d.missing ? 'no data' : `${format(vals[i])}${unit}`}`}
                        style={{ flex: 1, height: '100%', justifyContent: 'flex-end' }}
                    >
                        <View style={{
                            height: d.missing ? 2 : Math.max(vals[i] ? 3 : 1, (vals[i] / max) * (height - 4)),
                            backgroundColor: fg(color),
                            opacity: d.missing ? 0.15 : hover == null || hover === i ? 0.9 : 0.45,
                            borderTopLeftRadius: 4, borderTopRightRadius: 4,
                        }} />
                    </PressableScale>
                ))}
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={s.axis}>{data[0]?.day?.slice(5) || ''}</Text>
                <Text style={s.axis}>{data[data.length - 1]?.day?.slice(5) || ''}</Text>
            </View>
        </View>
    );
}

/** Retention cohorts: one hue, intensity = % retained, value printed in each cell. */
export function RetentionGrid({ cohorts = [] }) {
    const weeks = Math.max(0, ...cohorts.map((c) => c.retained.length));
    if (!cohorts.length) return <Text style={s.empty}>No cohorts yet. Each signup week becomes a row once tracked users exist.</Text>;
    return (
        <View style={{ gap: 3 }}>
            <View style={{ flexDirection: 'row', gap: 3 }}>
                <Text style={[s.axis, { width: 92 }]}>Signup week</Text>
                <Text style={[s.axis, { width: 44 }]}>Users</Text>
                {Array.from({ length: weeks }, (_, k) => <Text key={k} style={[s.axis, { width: 46, textAlign: 'center' }]}>W{k}</Text>)}
            </View>
            {cohorts.map((c) => (
                <View key={c.week} style={{ flexDirection: 'row', gap: 3, alignItems: 'center' }}>
                    <Text style={[s.cellText, { width: 92 }]}>{c.week.slice(5)}</Text>
                    <Text style={[s.cellText, { width: 44 }]}>{c.size}</Text>
                    {c.retained.map((pct, k) => (
                        <View key={k} accessibilityLabel={`Week ${k}: ${pct}%`} style={{ width: 46, height: 26, borderRadius: 4, alignItems: 'center', justifyContent: 'center', backgroundColor: `rgba(52,211,153,${0.08 + (pct / 100) * 0.62})` }}>
                            <Text style={[s.cellText, { color: pct >= 55 ? '#0B0B12' : AV.ink, fontWeight: '700' }]}>{Math.round(pct)}%</Text>
                        </View>
                    ))}
                </View>
            ))}
        </View>
    );
}

/** Horizontal labeled bars (features, sources, funnel). */
export function HBars({ rows = [], color = A.accent, showPct = false, total }) {
    const max = Math.max(1, ...rows.map((r) => r.count));
    const base = total || rows[0]?.count || 1;
    return (
        <View style={{ gap: 8 }}>
            {rows.map((r, i) => (
                <Appear key={r.name || r.step} index={i} from="right" distance={10}>
                    <View style={s.hrow}>
                        <Text style={s.hlabel} numberOfLines={1}>{r.name || r.step}</Text>
                        <View style={s.htrack}>
                            <View style={[s.hfill, { width: `${(r.count / max) * 100}%`, backgroundColor: fg(color) }]} />
                        </View>
                        <Text style={s.hvalue}>{r.count.toLocaleString('en-US')}{showPct && i > 0 ? `  ${Math.round((r.count / base) * 100)}%` : ''}</Text>
                    </View>
                </Appear>
            ))}
            {!rows.length && <Text style={s.empty}>No data yet.</Text>}
        </View>
    );
}

export function Panel({ title, subtitle, right, children, style }) {
    return (
        <View style={[s.panel, style]}>
            {(title || right) && (
                <View style={s.panelHead}>
                    <View style={{ flex: 1 }}>
                        {!!title && <Text style={s.panelTitle}>{title}</Text>}
                        {!!subtitle && <Text style={s.panelSub}>{subtitle}</Text>}
                    </View>
                    {right}
                </View>
            )}
            {children}
        </View>
    );
}

export function Btn({ label, onPress, tone = 'default', disabled, small, accessibilityLabel }) {
    const c = tone === 'primary' ? A.accent : tone === 'danger' ? A.red : tone === 'good' ? A.green : 'rgba(255,255,255,0.75)';
    return (
        <PressableScale onPress={onPress} disabled={disabled} accessibilityRole="button" accessibilityLabel={accessibilityLabel || label}
            style={[s.btn, small && s.btnSmall, { borderColor: tint(c, '66'), backgroundColor: tint(c, '14') }]} hoverStyle={{ backgroundColor: tint(c, '26') }}>
            <Text style={[s.btnText, small && { fontSize: 11 }, { color: isAccent(c) ? uiVar('adm-on-accent', c) : c }]}>{label}</Text>
        </PressableScale>
    );
}

export function Segments({ options, value, onChange }) {
    return (
        <View style={s.segs} accessibilityRole="tablist">
            {options.map((o) => {
                const on = o.value === value;
                return (
                    <PressableScale key={o.value} onPress={() => onChange(o.value)} lift={0} accessibilityRole="tab" accessibilityState={{ selected: on }}
                        style={[s.seg, on && s.segOn]}>
                        <Text style={[s.segText, on && { color: uiVar('adm-seg-on-ink', A.ink) }]}>{o.label}</Text>
                    </PressableScale>
                );
            })}
        </View>
    );
}

export function EmptyState({ icon = '◌', title, body }) {
    return (
        <View style={s.emptyBox}>
            <Text style={{ fontSize: 22, color: AV.faint }}>{icon}</Text>
            <Text style={s.emptyTitle}>{title}</Text>
            {!!body && <Text style={s.emptyBody}>{body}</Text>}
        </View>
    );
}

export { Circle };

const s = StyleSheet.create({
    pill: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: uiVar('adm-radius-pill', '999px'), borderWidth: 1, alignSelf: 'flex-start' },
    pillText: { fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
    stat: { flexGrow: 1, flexBasis: 150, padding: 14, borderRadius: uiVar('adm-radius-lg', '14px'), backgroundColor: AV.panel, borderWidth: 1, borderColor: AV.line, borderTopWidth: uiVar('adm-rule-w', '1px'), borderTopColor: uiVar('adm-rule', A.line), gap: 4 },
    statLabel: { color: AV.faint, fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
    statValue: { fontSize: 26, fontWeight: '800', letterSpacing: -0.5, ...ADM_DISPLAY },
    statHint: { color: AV.faint, fontSize: 11 },
    panel: { padding: 16, borderRadius: uiVar('adm-radius-lg', '16px'), backgroundColor: AV.panel, borderWidth: 1, borderColor: AV.line, borderTopWidth: uiVar('adm-rule-w', '1px'), borderTopColor: uiVar('adm-rule', A.line), gap: 12 },
    panelHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    panelTitle: { color: AV.ink, fontSize: 15, fontWeight: '800', ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal') },
    panelSub: { color: AV.faint, fontSize: 12, marginTop: 2 },
    hrow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    hlabel: { width: 130, color: AV.soft, fontSize: 12 },
    htrack: { flex: 1, height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.05)', overflow: 'hidden' },
    hfill: { height: 8, borderRadius: 4, opacity: 0.85 },
    hvalue: { minWidth: 70, textAlign: 'right', color: AV.ink, fontSize: 12, fontVariant: ['tabular-nums'] },
    empty: { color: AV.faint, fontSize: 12 },
    readout: { color: AV.soft, fontSize: 12, fontVariant: ['tabular-nums'] },
    axis: { color: AV.faint, fontSize: 10 },
    cellText: { color: AV.soft, fontSize: 11, fontVariant: ['tabular-nums'] },
    btn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: uiVar('adm-radius-md', '9px'), borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
    btnSmall: { paddingHorizontal: 9, paddingVertical: 5, borderRadius: uiVar('adm-radius-sm', '7px') },
    btnText: { fontSize: 12, fontWeight: '700', textTransform: uiVar('adm-display-case', 'none'), letterSpacing: uiVar('adm-label-track', 'normal') },
    segs: { flexDirection: 'row', padding: 3, borderRadius: uiVar('adm-radius-md', '10px'), backgroundColor: uiVar('adm-seg-bg', 'rgba(255,255,255,0.05)'), borderWidth: 1, borderColor: uiVar('adm-seg-border', A.line), alignSelf: 'flex-start', flexWrap: 'wrap' },
    seg: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: uiVar('adm-radius-sm', '7px') },
    segOn: { backgroundColor: uiVar('adm-seg-on-bg', 'rgba(167,139,250,0.22)') },
    segText: { color: AV.faint, fontSize: 12, fontWeight: '700', textTransform: uiVar('adm-display-case', 'none'), letterSpacing: uiVar('adm-label-track', 'normal') },
    emptyBox: { alignItems: 'center', padding: 28, gap: 6 },
    emptyTitle: { color: AV.ink, fontSize: 14, fontWeight: '700' },
    emptyBody: { color: AV.faint, fontSize: 12, textAlign: 'center', maxWidth: 420, lineHeight: 18 },
});
